import { MutableRefObject, useEffect, useRef } from 'react';

export type KeyboardShortcutConfig = {
  keys: string | string[];
  handler?: (event: KeyboardEvent) => void;
  disabled?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
};

const toArray = (keys: string | string[]) =>
  Array.isArray(keys) ? keys : [keys];

type ShortcutListenerEntry = {
  id: symbol;
  ref: MutableRefObject<KeyboardShortcutConfig[]>;
};

const listeners: ShortcutListenerEntry[] = [];
let isSubscribed = false;

const handleKeyDown = (event: KeyboardEvent) => {
  [...listeners].reverse().some(({ ref }) => {
    const shortcuts = ref.current;

    if (!shortcuts?.length) {
      return false;
    }

    return shortcuts.some((shortcut) => {
      if (!shortcut) {
        return false;
      }

      const {
        keys,
        handler,
        disabled,
        preventDefault = true,
        stopPropagation = true,
      } = shortcut;

      if (!keys || !toArray(keys).includes(event.key)) {
        return false;
      }

      if (preventDefault) {
        event.preventDefault();
      }

      if (stopPropagation) {
        event.stopPropagation();
        event.stopImmediatePropagation?.();
      }

      if (!disabled) {
        handler?.(event);
      }

      return true;
    });
  });
};

const subscribe = () => {
  if (isSubscribed || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('keydown', handleKeyDown);
  isSubscribed = true;
};

const unsubscribe = () => {
  if (!isSubscribed || listeners.length > 0 || typeof window === 'undefined') {
    return;
  }

  window.removeEventListener('keydown', handleKeyDown);
  isSubscribed = false;
};

export default function useArrowKeyNavigation(
  shortcuts: KeyboardShortcutConfig[] = [],
) {
  const shortcutsRef = useRef<KeyboardShortcutConfig[]>(shortcuts);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const id = Symbol('keyboard-shortcut-listener');
    listeners.push({ id, ref: shortcutsRef });
    subscribe();

    return () => {
      const nextListeners = listeners.filter((entry) => entry.id !== id);
      listeners.length = 0;
      listeners.push(...nextListeners);
      unsubscribe();
    };
  }, []);
}
